import axios from "axios";
import { routes } from "@/router/Routes";
import { AUTH_BASE_URL } from "@/common/config";
import { APIService } from "./APIService";
import type { UserProfile } from "@/models/UserProfile";

const authBaseUrl = AUTH_BASE_URL;

export class ProfileService extends APIService {
  private static instance: ProfileService;

  protected readonly authBaseUrl = authBaseUrl;

  private constructor() {
    super();
  }

  public static getInstance(): ProfileService {
    if (!ProfileService.instance) {
      ProfileService.instance = new ProfileService();
    }
    return ProfileService.instance;
  }

  private readonly sendRequest = async (url: string, data: unknown, method = "POST") => {
    const formData = new FormData();

    if (typeof data === "object" && data !== null) {
      for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined) continue;

        if (key === "access_token") {
          formData.append(key, String(value).replace(/^"|"$/g, ""));
        } else if (typeof value === "object" && !(value instanceof File)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value as string | Blob);
        }
      }
    }

    return axios({
      method,
      url: this.authBaseUrl + url,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Set-Cookie": "true",
      },
      withCredentials: true,
    });
  };

  public readonly getProfile = async (accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.get,
      {
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly updateProfile = async (profile: Partial<UserProfile>, accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.update,
      {
        ...profile,
        access_token: accessToken,
      },
      "PUT",
    );
  };

  public readonly enable2FA = async (accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.enable2FA,
      {
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly verify2FA = async (code: string, accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.verify2FA,
      {
        code: code,
        access_token: accessToken,
      },
      "POST",
    );
  };
  public readonly deleteAccount = async (accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.deleteAccount,
      {
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly disable2FA = async (accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.disable2FA,
      {
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly uploadAvatar = async (file: File, accessToken: string) => {
    return this.sendRequest(
      routes.auth.profile.uploadAvatar,
      {
        avatar: file,
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly revokeSession = async (sessionId?: string, accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.sessionRevoke,
      {
        session_id: sessionId,
        access_token: accessToken,
      },
      "POST",
    );
  };

  public readonly passwordChange = async (current_password: string, new_password: string, accessToken?: string) => {
    return this.sendRequest(
      routes.auth.profile.passwordChange,
      {
        current_password: current_password,
        new_password: new_password,
        access_token: accessToken,
      },
      "POST",
    );
  };
}
